import { useState, useRef, useEffect } from "react";
import "./ThemeManager.css";

export const PRESET_CHAT_COLORS = {
  default: { own: "#d46aa8", ownText: "#ffffff", other: "#ffffff", otherText: "#1c1e21" },
  dark: { own: "#6b8cae", ownText: "#ffffff", other: "#3a3b3c", otherText: "#e4e6ea" },
  light: { own: "#5b8def", ownText: "#ffffff", other: "#f0f2f5", otherText: "#1c1e21" },
  blue: { own: "#5a67e8", ownText: "#ffffff", other: "rgba(255,255,255,0.94)", otherText: "#1c1e21" },
  green: { own: "#0f8f7f", ownText: "#ffffff", other: "rgba(255,255,255,0.94)", otherText: "#1c1e21" },
  purple: { own: "#8e2de2", ownText: "#ffffff", other: "rgba(255,255,255,0.94)", otherText: "#1c1e21" },
  sunset: { own: "#e86b7a", ownText: "#ffffff", other: "rgba(255,255,255,0.94)", otherText: "#1c1e21" },
  custom: { own: "#c75b9a", ownText: "#ffffff", other: "rgba(255,255,255,0.94)", otherText: "#1c1e21" },
};

const IMAGE_CHAT_COLOR_BY_URL = {
  "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=300&fit=crop": "#e67e22",
  "https://images.unsplash.com/photo-1534796636912-3b95b3ab5986?w=400&h=300&fit=crop": "#6c5ce7",
  "https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=400&h=300&fit=crop": "#0984e3",
  "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=400&h=300&fit=crop": "#27ae60",
  "https://images.unsplash.com/photo-1519501025264-65ba15a82390?w=400&h=300&fit=crop": "#e17055",
  "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=300&fit=crop": "#5d6d7e",
  "https://plus.unsplash.com/premium_photo-1682309735318-934795084028?w=1212&auto=format&fit=crop": "#e67e22",
  "https://images.unsplash.com/photo-1572085313466-6710de8d7ba3?w=735&auto=format&fit=crop": "#c0392b",
  "https://images.unsplash.com/photo-1522383225653-ed111181a951?w=400&h=300&fit=crop": "#e84393",
  "https://images.unsplash.com/photo-1597848212624-a19eb35e2651?w=400&h=300&fit=crop": "#d68910",
  "https://images.unsplash.com/photo-1499002238440-d264edd596ec?w=400&h=300&fit=crop": "#9b59b6",
  "https://images.unsplash.com/photo-1526047932273-341f2a7631f9?w=400&h=300&fit=crop": "#e74c3c",
  "https://images.unsplash.com/photo-1490750967868-88aa4486c946?w=400&h=300&fit=crop": "#e84393",
  "https://images.unsplash.com/photo-1747738305505-66f6e3781265?w=400&h=300&fit=crop": "#8e44ad",
  "https://images.unsplash.com/photo-1541961017774-22349e4a1262?w=400&h=300&fit=crop": "#e17055",
  "https://images.unsplash.com/photo-1735747637427-c3e0ef33e43e?w=752&auto=format&fit=crop": "#e84393",
  "https://images.unsplash.com/photo-1623057896740-99f8c2f090fc?w=1170&auto=format&fit=crop": "#3d7ea6",
};

export function getThemeChatColors(theme) {
  if (!theme) return PRESET_CHAT_COLORS.default;
  const fallback = PRESET_CHAT_COLORS[theme.type] || PRESET_CHAT_COLORS.custom;
  const imageColor = theme.customImage ? IMAGE_CHAT_COLOR_BY_URL[theme.customImage] : null;
  return {
    own: theme.chatColor || imageColor || fallback.own,
    ownText: theme.ownText || fallback.ownText,
    other: theme.incomingColor || fallback.other,
    otherText: theme.incomingText || fallback.otherText,
  };
}

const withChatColors = (themePayload, extra = {}) => {
  const merged = { ...themePayload, ...extra };
  const colors = getThemeChatColors(merged);
  return {
    ...merged,
    chatColor: colors.own,
    ownText: colors.ownText,
    incomingColor: colors.other,
    incomingText: colors.otherText,
  };
};

export default function ThemeManager({ 
  isOpen, 
  onClose, 
  currentTheme, 
  onThemeChange,
  conversationId 
}) {
  const [selectedTheme, setSelectedTheme] = useState(currentTheme?.type || 'default');
  const [customImage, setCustomImage] = useState(currentTheme?.customImage || null);
  const [imagePreview, setImagePreview] = useState(currentTheme?.type === 'custom' ? currentTheme?.customImage : null);
  const [opacity, setOpacity] = useState(currentTheme?.opacity ?? 0.3);
  const [blur, setBlur] = useState(currentTheme?.blur || 0);
  const [activeTab, setActiveTab] = useState('presets'); // 'presets', 'gallery', 'upload'
  const [selectedChatColor, setSelectedChatColor] = useState(currentTheme?.chatColor || null);
  const fileInputRef = useRef(null);

  const presetThemes = [
    {
      id: 'default',
      name: 'Default',
      background: 'var(--bg-primary)',
      preview: 'linear-gradient(135deg, #f3c6e4 0%, #d46aa8 100%)',
      chatColor: PRESET_CHAT_COLORS.default.own,
    },
    {
      id: 'dark',
      name: 'Dark',
      background: '#1a1a1a',
      preview: 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)',
      chatColor: PRESET_CHAT_COLORS.dark.own,
    },
    {
      id: 'light',
      name: 'Light',
      background: '#ffffff',
      preview: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
      chatColor: PRESET_CHAT_COLORS.light.own,
    },
    {
      id: 'blue',
      name: 'Ocean Blue',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      preview: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      chatColor: PRESET_CHAT_COLORS.blue.own,
    },
    {
      id: 'green',
      name: 'Forest Green',
      background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
      preview: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
      chatColor: PRESET_CHAT_COLORS.green.own,
    },
    {
      id: 'purple',
      name: 'Purple Haze',
      background: 'linear-gradient(135deg, #8e2de2 0%, #4a00e0 100%)',
      preview: 'linear-gradient(135deg, #8e2de2 0%, #4a00e0 100%)',
      chatColor: PRESET_CHAT_COLORS.purple.own,
    },
    {
      id: 'sunset',
      name: 'Sunset',
      background: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 50%, #fecfef 100%)',
      preview: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 50%, #fecfef 100%)',
      chatColor: PRESET_CHAT_COLORS.sunset.own,
    },
    {
      id: 'custom',
      name: 'Custom Image',
      background: 'transparent',
      preview: 'linear-gradient(135deg, #ddd 0%, #ccc 100%)',
      chatColor: PRESET_CHAT_COLORS.custom.own,
    }
  ];

  const cartoonImages = [
    { id: 'cartoon1', name: 'Cute Animals', url: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=300&fit=crop', chatColor: '#e67e22' },
    { id: 'cartoon2', name: 'Space Adventure', url: 'https://images.unsplash.com/photo-1534796636912-3b95b3ab5986?w=400&h=300&fit=crop', chatColor: '#6c5ce7' },
    { id: 'cartoon3', name: 'Underwater World', url: 'https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=400&h=300&fit=crop', chatColor: '#0984e3' },
    { id: 'cartoon4', name: 'Forest Friends', url: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=400&h=300&fit=crop', chatColor: '#27ae60' },
    { id: 'cartoon5', name: 'City Lights', url: 'https://images.unsplash.com/photo-1519501025264-65ba15a82390?w=400&h=300&fit=crop', chatColor: '#e17055' },
    { id: 'cartoon6', name: 'Mountain View', url: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=300&fit=crop', chatColor: '#5d6d7e' },
    { id: 'cartoon7', name: 'Happy face', url: 'https://plus.unsplash.com/premium_photo-1682309735318-934795084028?w=1212&auto=format&fit=crop', chatColor: '#e67e22' }
  ];

  const flowerImages = [
    { id: 'flower1', name: 'Rose Garden', url: 'https://images.unsplash.com/photo-1572085313466-6710de8d7ba3?w=735&auto=format&fit=crop', chatColor: '#c0392b' },
    { id: 'flower2', name: 'Cherry Blossoms', url: 'https://images.unsplash.com/photo-1522383225653-ed111181a951?w=400&h=300&fit=crop', chatColor: '#e84393' },
    { id: 'flower3', name: 'Sunflowers', url: 'https://images.unsplash.com/photo-1597848212624-a19eb35e2651?w=400&h=300&fit=crop', chatColor: '#d68910' },
    { id: 'flower4', name: 'Lavender Field', url: 'https://images.unsplash.com/photo-1499002238440-d264edd596ec?w=400&h=300&fit=crop', chatColor: '#9b59b6' },
    { id: 'flower5', name: 'Tulip Garden', url: 'https://images.unsplash.com/photo-1526047932273-341f2a7631f9?w=400&h=300&fit=crop', chatColor: '#e74c3c' },
    { id: 'flower6', name: 'Wild Flowers', url: 'https://images.unsplash.com/photo-1490750967868-88aa4486c946?w=400&h=300&fit=crop', chatColor: '#e84393' },
    { id: 'flower7', name: 'Delicate Iris', url: 'https://images.unsplash.com/photo-1747738305505-66f6e3781265?w=400&h=300&fit=crop', chatColor: '#8e44ad' }
  ];

  const galleryImages = [
    { id: 'gallery1', name: 'Abstract Art', url: 'https://images.unsplash.com/photo-1541961017774-22349e4a1262?w=400&h=300&fit=crop', chatColor: '#e17055' },
    { id: 'gallery2', name: 'Minimalist', url: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=300&fit=crop', chatColor: '#636e72' },
    { id: 'gallery3', name: 'Vintage', url: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=400&h=300&fit=crop', chatColor: '#b08968' },
    { id: 'gallery4', name: 'Nature', url: 'https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=400&h=300&fit=crop', chatColor: '#1abc9c' },
    { id: 'gallery5', name: 'Urban', url: 'https://images.unsplash.com/photo-1519501025264-65ba15a82390?w=400&h=300&fit=crop', chatColor: '#2d3436' },
    { id: 'gallery6', name: 'bunch of heart and star balloons', url: 'https://images.unsplash.com/photo-1735747637427-c3e0ef33e43e?w=752&auto=format&fit=crop', chatColor: '#e84393' },
    { id: 'gallery7', name: 'Snowy Mountain', url: 'https://images.unsplash.com/photo-1623057896740-99f8c2f090fc?w=1170&auto=format&fit=crop', chatColor: '#3d7ea6' }
  ];

  const previewColors = getThemeChatColors({
    type: selectedTheme,
    chatColor: selectedChatColor || (selectedTheme === 'custom' ? PRESET_CHAT_COLORS.custom.own : undefined),
  });
  
  useEffect(() => {
    if (!isOpen) return;
    setSelectedTheme(currentTheme?.type || "default");
    setCustomImage(currentTheme?.customImage || null);
    setImagePreview(currentTheme?.type === "custom" ? currentTheme?.customImage || null : null);
    setOpacity(currentTheme?.opacity ?? 0.3);
    setBlur(currentTheme?.blur || 0);
    setSelectedChatColor(currentTheme?.chatColor || null);
  }, [isOpen, currentTheme]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const popup = document.querySelector(".messenger_sidebar_container");
    popup?.classList.add("theme_picker_open");

    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    let lastTouchY = 0;

    const getScrollDelta = (event) => {
      if (event.type === "wheel") return event.deltaY;
      if (event.type === "touchmove" && event.touches?.[0]) {
        const currentY = event.touches[0].clientY;
        const delta = lastTouchY - currentY;
        lastTouchY = currentY;
        return delta;
      }
      return 0;
    };

    const onTouchStart = (event) => {
      if (event.touches?.[0]) {
        lastTouchY = event.touches[0].clientY;
      }
    };

    const preventBackgroundScroll = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) {
        event.preventDefault();
        return;
      }

      if (!target.closest(".theme_manager_overlay")) return;

      const scrollable = target.closest(
        ".theme_manager_content, .theme_options"
      );
      const delta = getScrollDelta(event);

      if (scrollable && scrollable.scrollHeight > scrollable.clientHeight + 1) {
        const atTop = scrollable.scrollTop <= 0;
        const atBottom =
          scrollable.scrollTop + scrollable.clientHeight >=
          scrollable.scrollHeight - 1;
        if ((delta < 0 && atTop) || (delta > 0 && atBottom)) {
          event.preventDefault();
        }
        return;
      }

      event.preventDefault();
    };

    document.addEventListener("touchstart", onTouchStart, {
      passive: true,
      capture: true,
    });
    document.addEventListener("wheel", preventBackgroundScroll, {
      passive: false,
      capture: true,
    });
    document.addEventListener("touchmove", preventBackgroundScroll, {
      passive: false,
      capture: true,
    });

    return () => {
      popup?.classList.remove("theme_picker_open");
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
      document.removeEventListener("touchstart", onTouchStart, { capture: true });
      document.removeEventListener("wheel", preventBackgroundScroll, {
        capture: true,
      });
      document.removeEventListener("touchmove", preventBackgroundScroll, {
        capture: true,
      });
    };
  }, [isOpen]);

  const handleThemeSelect = (theme) => {
    setSelectedTheme(theme.id);
    setSelectedChatColor(theme.chatColor || PRESET_CHAT_COLORS[theme.id]?.own || null);
    if (theme.id !== 'custom') {
      setCustomImage(null);
      setImagePreview(null);
      onThemeChange(withChatColors({
        type: theme.id,
        background: theme.background,
        customImage: null,
        opacity: 0.3,
        blur: 0,
        chatColor: theme.chatColor,
      }));
    }
  };

  const handleImageSelect = (image) => {
    setCustomImage(image.url);
    setSelectedTheme('custom');
    setSelectedChatColor(image.chatColor || PRESET_CHAT_COLORS.custom.own);
    onThemeChange(withChatColors({
      type: 'custom',
      background: 'transparent',
      customImage: image.url,
      opacity: opacity,
      blur: blur,
      chatColor: image.chatColor || PRESET_CHAT_COLORS.custom.own,
    }));
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setCustomImage(e.target.result);
        setImagePreview(e.target.result);
        setSelectedTheme('custom');
        setSelectedChatColor(PRESET_CHAT_COLORS.custom.own);
        onThemeChange(withChatColors({
          type: 'custom',
          background: 'transparent',
          customImage: e.target.result,
          opacity: opacity,
          blur: blur,
          chatColor: PRESET_CHAT_COLORS.custom.own,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleOpacityChange = (value) => {
    setOpacity(value);
    if (selectedTheme === 'custom' && customImage) {
      onThemeChange(withChatColors({
        type: 'custom',
        background: 'transparent',
        customImage: customImage,
        opacity: value,
        blur: blur,
        chatColor: selectedChatColor || PRESET_CHAT_COLORS.custom.own,
      }));
    }
  };

  const handleBlurChange = (value) => {
    setBlur(value);
    if (selectedTheme === 'custom' && customImage) {
      onThemeChange(withChatColors({
        type: 'custom',
        background: 'transparent',
        customImage: customImage,
        opacity: opacity,
        blur: value,
        chatColor: selectedChatColor || PRESET_CHAT_COLORS.custom.own,
      }));
    }
  };

  const handleRemoveImage = () => {
    setCustomImage(null);
    setImagePreview(null);
    setSelectedTheme('default');
    setSelectedChatColor(PRESET_CHAT_COLORS.default.own);
    onThemeChange(withChatColors({
      type: 'default',
      background: 'var(--bg-primary)',
      customImage: null,
      opacity: 0.3,
      blur: 0,
      chatColor: PRESET_CHAT_COLORS.default.own,
    }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSaveTheme = () => {
    const themeData = {
      conversationId,
      theme: withChatColors({
        type: selectedTheme,
        background: selectedTheme === 'custom' ? 'transparent' : presetThemes.find(t => t.id === selectedTheme)?.background,
        customImage: selectedTheme === 'custom' ? customImage : null,
        opacity: opacity,
        blur: blur,
        chatColor: selectedChatColor || PRESET_CHAT_COLORS[selectedTheme]?.own,
      })
    };
    
    const savedThemes = JSON.parse(localStorage.getItem('messengerThemes') || '{}');
    savedThemes[conversationId] = themeData.theme;
    localStorage.setItem('messengerThemes', JSON.stringify(savedThemes));
    onClose();
  };

  const handleResetTheme = () => {
    setSelectedTheme('default');
    setCustomImage(null);
    setSelectedChatColor(PRESET_CHAT_COLORS.default.own);
    setOpacity(0.3);
    setBlur(0);
    onThemeChange(withChatColors({
      type: 'default',
      background: 'var(--bg-primary)',
      customImage: null,
      opacity: 0.3,
      blur: 0,
      chatColor: PRESET_CHAT_COLORS.default.own,
    }));
    
    // Remove from localStorage
    const savedThemes = JSON.parse(localStorage.getItem('messengerThemes') || '{}');
    delete savedThemes[conversationId];
    localStorage.setItem('messengerThemes', JSON.stringify(savedThemes));
  };

  if (!isOpen) return null;

  return (
    <div className="theme_manager_overlay" onClick={onClose}>
      <div className="theme_manager_modal" onClick={(e) => e.stopPropagation()}>
        <div className="theme_manager_header">
          <h3>Chat Theme</h3>
          <button className="close_btn" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>

        <div className="theme_manager_content">
          <div className="theme_preview">
            <div
              className="theme_preview_area"
              style={{
                '--preview-own': previewColors.own,
                '--preview-own-text': previewColors.ownText,
                '--preview-other': previewColors.other,
                '--preview-other-text': previewColors.otherText,
              }}
            >
              <div
                className="theme_preview_bg"
                style={{
                  background: selectedTheme === 'custom' && customImage
                    ? `linear-gradient(rgba(248,218,240,${opacity}), rgba(248,218,240,${opacity})), url(${customImage})`
                    : presetThemes.find((t) => t.id === selectedTheme)?.background || 'var(--bg-primary)',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  filter: selectedTheme === 'custom' && customImage ? `blur(${blur}px)` : 'none',
                }}
              />
              <span className="theme_preview_label">Preview</span>
              <div className="preview_chat">
                <div className="preview_message incoming">
                  <div className="preview_avatar" />
                  <div className="preview_bubble">
                    <p>Hey! How are you?</p>
                  </div>
                </div>
                <div className="preview_message outgoing">
                  <div className="preview_bubble own">
                    <p>This is how your chat will look!</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="theme_tabs" role="tablist" aria-label="Theme categories">
            <button
              type="button"
              className={`tab_btn ${activeTab === 'presets' ? 'active' : ''}`}
              onClick={() => setActiveTab('presets')}
            >
              Preset Themes
            </button>
            <button
              type="button"
              className={`tab_btn ${activeTab === 'cartoons' ? 'active' : ''}`}
              onClick={() => setActiveTab('cartoons')}
            >
              Cartoons
            </button>
            <button
              type="button"
              className={`tab_btn ${activeTab === 'flowers' ? 'active' : ''}`}
              onClick={() => setActiveTab('flowers')}
            >
              Flowers
            </button>
            <button
              type="button"
              className={`tab_btn ${activeTab === 'gallery' ? 'active' : ''}`}
              onClick={() => setActiveTab('gallery')}
            >
              Gallery
            </button>
            <button
              type="button"
              className={`tab_btn ${activeTab === 'upload' ? 'active' : ''}`}
              onClick={() => setActiveTab('upload')}
            >
              Upload
            </button>
          </div>

          <div className="theme_options">
            {activeTab === 'presets' && (
              <div className="preset_themes">
                {presetThemes.map((theme) => {
                  const isSelected =
                    theme.id === 'custom'
                      ? selectedTheme === 'custom' && !!customImage
                      : selectedTheme === theme.id;
                  const thumbStyle =
                    theme.id === 'custom' && customImage
                      ? { backgroundImage: `url(${customImage})`, '--theme-own': theme.chatColor }
                      : { background: theme.preview || theme.background, '--theme-own': theme.chatColor };

                  return (
                    <button
                      key={theme.id}
                      type="button"
                      className={`theme_option ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleThemeSelect(theme)}
                    >
                      <div className="theme_preview_thumb" style={thumbStyle}>
                        {isSelected && (
                          <span className="theme_thumb_check" aria-hidden="true">
                            ✓
                          </span>
                        )}
                      </div>
                      <span>{theme.name}</span>
                    </button>
                  );
                })}
              </div>
            )}

            {activeTab === 'cartoons' && (
              <div className="image_grid">
                {cartoonImages.map((image) => {
                  const isSelected = customImage === image.url;
                  return (
                    <button
                      key={image.id}
                      type="button"
                      className={`image_option ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleImageSelect(image)}
                      style={{ '--theme-own': image.chatColor }}
                    >
                      <div className="image_option_thumb">
                        <img src={image.url} alt="" />
                        {isSelected && (
                          <span className="theme_thumb_check" aria-hidden="true">
                            ✓
                          </span>
                        )}
                      </div>
                      <span>{image.name}</span>
                    </button>
                  );
                })}
              </div>
            )}

            {activeTab === 'flowers' && (
              <div className="image_grid">
                {flowerImages.map((image) => {
                  const isSelected = customImage === image.url;
                  return (
                    <button
                      key={image.id}
                      type="button"
                      className={`image_option ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleImageSelect(image)}
                      style={{ '--theme-own': image.chatColor }}
                    >
                      <div className="image_option_thumb">
                        <img src={image.url} alt="" />
                        {isSelected && (
                          <span className="theme_thumb_check" aria-hidden="true">
                            ✓
                          </span>
                        )}
                      </div>
                      <span>{image.name}</span>
                    </button>
                  );
                })}
              </div>
            )}

            {activeTab === 'gallery' && (
              <div className="image_grid">
                {galleryImages.map((image) => {
                  const isSelected = customImage === image.url;
                  return (
                    <button
                      key={image.id}
                      type="button"
                      className={`image_option ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleImageSelect(image)}
                      style={{ '--theme-own': image.chatColor }}
                    >
                      <div className="image_option_thumb">
                        <img src={image.url} alt="" />
                        {isSelected && (
                          <span className="theme_thumb_check" aria-hidden="true">
                            ✓
                          </span>
                        )}
                      </div>
                      <span>{image.name}</span>
                    </button>
                  );
                })}
              </div>
            )}

            {activeTab === 'upload' && (
              <>
                <h4>Upload Custom Image</h4>
                <div className="custom_image_section">
                  <div className="image_upload_area">
                    {customImage ? (
                      <div className="image_preview_container">
                        <img 
                          src={customImage} 
                          alt="Custom background" 
                          className="uploaded_image_preview"
                        />
                        <button 
                          className="remove_image_btn"
                          onClick={handleRemoveImage}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                          </svg>
                        </button>
                      </div>
                    ) : (
                      <div 
                        className="upload_placeholder"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M19 7v2.99s-1.99.01-2 0V7h-3s.01-1.99 0-2h3V2h2v3h3v2h-3zm-3 4V8h-3V5H5c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2v-8h-3zM5 19l3-4 2 3 3-4 4 5H5z"/>
                        </svg>
                        <p>Click to upload image</p>
                      </div>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      style={{ display: 'none' }}
                    />
                  </div>

                  {imagePreview && (
                    <div className="image_settings">
                      <div className="setting_group">
                        <label>Opacity: {opacity}</label>
                        <input
                          type="range"
                          min="0"
                          max="0.8"
                          step="0.1"
                          value={opacity}
                          onChange={(e) => handleOpacityChange(parseFloat(e.target.value))}
                          className="slider"
                        />
                      </div>
                      <div className="setting_group">
                        <label>Blur: {blur}px</label>
                        <input
                          type="range"
                          min="0"
                          max="10"
                          step="1"
                          value={blur}
                          onChange={(e) => handleBlurChange(parseInt(e.target.value))}
                          className="slider"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="theme_manager_actions">
          <button type="button" className="reset_btn" onClick={handleResetTheme}>
            Reset
          </button>
          <button type="button" className="cancel_btn" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="save_btn" onClick={handleSaveTheme}>
            Apply Theme
          </button>
        </div>
      </div>
    </div>
  );
} 